import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import * as SaveClipUtils from 'src/components/SavePanel/utils/saveClipUtils';
import * as EventUtils from 'utils/EventUtil';
import { CLOUD_STATUS_ERROR } from 'vd-constants';

function useShareHasMissingVideo(event: IEvent, tenantId: string) {
  const [shareHasMissingVideo, setShareHasMissingVideo] = React.useState(false);

  const { cameras, shareStartTime, shareEndTime, id: eventId, locationId } = event;
  const isShare = EventUtils.isShared(event);
  const isPushComplete = EventUtils.isPushToCloudComplete(event);
  /** -1 is not a reliable flag to determine if a stream has video pushed to cloud
   * eg. if the video is already in cloud, the totalSegments will be -1 (for now)
   */
  // A new array each render would refetch on every parent render (e.g. column resize).
  const camerasMayMissVideo = React.useMemo(
    () =>
      cameras.filter((camera) =>
        camera.streams.some((stream) => stream.totalSegments === -1 || stream.totalSegments === 0),
      ),
    [cameras],
  );

  React.useEffect(() => {
    let cancelled = false;

    async function getPushToCloudStatus(cameras: IEvent['cameras']) {
      for (const camera of cameras) {
        if (cancelled) return;
        const savedStream = camera.streams.find((stream) => stream.totalSegments != null);
        if (!locationId) {
          console.error('No locationId found for the event');
          continue;
        }
        if (!savedStream) {
          console.error('No stream found for the camera');
          continue;
        }
        if (!shareStartTime || !shareEndTime) {
          console.error('No share start or end time found for the event');
          continue;
        }
        try {
          const response = await SaveClipUtils.getPushToCloudStatus({
            tenantId,
            eventId,
            deviceId: locationId,
            cameraId: camera.id,
            streamId: savedStream.id,
            start: new Date(shareStartTime).getTime(),
            end: new Date(shareEndTime).getTime(),
          });
          if (cancelled) return;
          if (response.totalSegments === 0 && response.state === CLOUD_STATUS_ERROR) {
            setShareHasMissingVideo(true);
            break;
          }
        } catch (error) {
          console.error('Error getting push to cloud status', error);
          continue;
        }
      }
    }

    if (isShare && isPushComplete) {
      getPushToCloudStatus(camerasMayMissVideo);
    }

    return () => {
      cancelled = true;
    };
  }, [
    eventId,
    tenantId,
    isShare,
    isPushComplete,
    camerasMayMissVideo,
    locationId,
    shareStartTime,
    shareEndTime,
  ]);

  return shareHasMissingVideo;
}

export default useShareHasMissingVideo;
